import React, { useEffect, useState } from 'react'
import FlcLogo from 'Assets/Images/TAS.png'
import { InputField } from 'JsUtils/FormikControls';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CircularProgress from '@mui/material/CircularProgress';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router';
import styles from './index.module.scss'
import Backdrop from '@mui/material/Backdrop';
import Button from '@mui/material/Button';
import { Container, Typography, Box } from '@mui/material';

// Import the API system
import API from '../../api';

const PasswordResetForm = () => {
    const navigate = useNavigate();
    const [backdrop, setBackdrop] = useState(false);
    const [userType, setUserType] = useState(""); // Default to advisor
    const [showMfaForm, setShowMfaForm] = useState(false); // Just like sign-in MFA state!
    const [userId, setUserId] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const { userRole } = useParams();

    useEffect(() => {
        if(!!userRole){
            setUserType(userRole);
        }
    }, [userRole])
    
    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleClickShowConfirmPassword = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    // STEP 1: User ID Form
    const userIdInitialValues = {
        userId: '',
    };

    const userIdValidationSchema = Yup.object({
        userId: Yup.string().required('This field is required'),
    });

    const HandleUserIdSubmit = (values) => {
        // Just collect user data and show MFA form - no API call yet!
        setUserId(values.userId);
        setShowMfaForm(true);
        toast.info('Please enter your authentication code and new password');
    };

    // STEP 2: MFA + Password Form
    const mfaPasswordInitialValues = {
        otpCode: '',
        newPassword: '',
        confirmPassword: '',
    };

    const mfaPasswordValidationSchema = Yup.object({
        otpCode: Yup.string().required('Authentication code is required')
            .min(6, 'Code must be at least 6 characters')
            .max(10, 'Code cannot be more than 10 characters'),
        newPassword: Yup.string()
            .required('New password is required')
            .min(8, 'Password must be at least 8 characters')
            .matches(
                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
            ),
        confirmPassword: Yup.string()
            .required('Please confirm your password')
            .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
    });

    const HandleMfaPasswordSubmit = async (values) => {
        setBackdrop(true);

        // Send everything to single API call!
        const resetData = {
            userId: userId,
            userType: userType,
            otpCode: values.otpCode,
            newPassword: values.newPassword
        };

        try {
            // Single API call with all data - use your existing resetPasswordDirect
            const response = await API.auth.resetPasswordDirect(resetData);
            
            if (response.status === 200 || response.status === 201) {
                toast.success('Password reset successfully! Redirecting to login...');
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            } else {
                toast.error(response.data.message || 'Failed to reset password');
                setBackdrop(false);
            }
        } catch (err) {
            setBackdrop(false);
            console.error('Password reset error:', err);
            toast.error(err?.response?.data?.message || 'Password reset failed');
        }
    };

    // RENDER USER ID FORM
    const renderUserIdForm = () => {
        return (
            <Formik 
                initialValues={userIdInitialValues} 
                validationSchema={userIdValidationSchema} 
                onSubmit={HandleUserIdSubmit}
            >
                {() => (
                    <Form>
                        <div className={styles.wrapper}>
                            <img src={FlcLogo} alt="TAS" height={'60px'} />
                            <h2>RESET PASSWORD</h2>
                        </div>

                        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ textAlign: 'center' }}>
                                Enter your User ID and select account type
                            </Typography>
                        </Box>

                        {/* USER TYPE SELECTOR */}
                        {/* <div className={styles.userTypeSelector}>
                            <FormControl component="fieldset" sx={{ width: '100%', mb: 2 }}>
                                <FormLabel component="legend" sx={{ color: '#0A48B3', fontWeight: 'bold' }}>
                                    Account Type
                                </FormLabel>
                                <RadioGroup 
                                    row 
                                    aria-label="user-type" 
                                    name="user-type" 
                                    value={userType} 
                                    onChange={handleUserTypeChange}
                                    sx={{ 
                                        justifyContent: 'space-around', 
                                        '& .MuiFormControlLabel-root': { margin: '0' } 
                                    }}
                                >
                                    <FormControlLabel 
                                        value="advisor" 
                                        control={<Radio color="primary" />} 
                                        label={
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <BusinessIcon sx={{ mr: 0.5 }} />
                                                <Typography>Advisor</Typography>
                                            </div>
                                        } 
                                    />
                                    <FormControlLabel 
                                        value="client" 
                                        control={<Radio color="primary" />} 
                                        label={
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <PersonIcon sx={{ mr: 0.5 }} />
                                                <Typography>Client</Typography>
                                            </div>
                                        } 
                                    />
                                </RadioGroup>
                            </FormControl>
                        </div> */}

                        {/* USER ID INPUT */}
                        <div className={styles.inputContainer}>
                            <InputField
                                label={"Email/Username*"}
                                name="userId"
                                type="text"
                                placeholder="Email or Username"
                                className={styles.inputField}
                                maxLength={50}
                                adornment={<PersonIcon />}
                            />
                        </div>

                        <div>
                            <Button 
                                sx={{ background: '#0A48B3', width: '100%', padding: '10px' }} 
                                type='submit' 
                                variant="contained"
                            >
                                Continue
                            </Button>
                        </div>

                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Typography 
                                variant="body2" 
                                sx={{ color: '#0A48B3', cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => navigate(`/sign-in/${userType}`)}
                            >
                                Back to Login
                            </Typography>
                        </Box>
                    </Form>
                )}
            </Formik>
        );
    };

    // RENDER MFA + PASSWORD FORM
    const renderMfaPasswordForm = () => {
        return (
            <Formik 
                initialValues={mfaPasswordInitialValues} 
                validationSchema={mfaPasswordValidationSchema} 
                onSubmit={HandleMfaPasswordSubmit}
            >
                {() => (
                    <Form>
                        <div className={styles.wrapper}>
                            <img src={FlcLogo} alt="TAS" height={'60px'} />
                            <h2>RESET PASSWORD</h2>
                        </div>

                        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ textAlign: 'center' }}>
                                Enter your authentication code and new password
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 2, p: 1, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ textAlign: 'center', color: '#1976d2' }}>
                                User: {userId} ({userType})
                            </Typography>
                        </Box>

                        {/* OTP CODE INPUT - FIRST */}
                        <div className={styles.inputContainer}>
                            <InputField
                                name="otpCode"
                                type="text"
                                placeholder="Authentication Code *"
                                className={styles.inputField}
                                maxLength={10}
                                adornment={<VerifiedUserIcon />}
                            />
                        </div>

                        {/* NEW PASSWORD - SECOND */}
                        <div className={styles.inputContainer}>
                            <InputField
                                name="newPassword"
                                type={showPassword ? "text" : "password"}
                                placeholder="New Password *"
                                className={styles.inputField}
                                maxLength={50}
                                adornment={<LockIcon />}
                                endAdornment={true}
                                showPassword={showPassword}
                                handleClickShowPassword={handleClickShowPassword}
                            />
                        </div>

                        {/* CONFIRM PASSWORD - THIRD */}
                        <div className={styles.inputContainer}>
                            <InputField
                                name="confirmPassword"
                                type={showConfirmPassword ? "text" : "password"}
                                placeholder="Confirm New Password *"
                                className={styles.inputField}
                                maxLength={50}
                                adornment={<LockIcon />}
                                endAdornment={true}
                                showPassword={showConfirmPassword}
                                handleClickShowPassword={handleClickShowConfirmPassword}
                            />
                        </div>

                        <Box sx={{ mb: 2, p: 1, backgroundColor: '#fff3cd', borderRadius: 1, border: '1px solid #ffeaa7' }}>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#856404' }}>
                                Password must contain at least 8 characters with uppercase, lowercase, number, and special character.
                            </Typography>
                        </Box>

                        <div>
                            <Button 
                                sx={{ background: '#0A48B3', width: '100%', padding: '10px' }} 
                                type='submit' 
                                variant="contained"
                            >
                                Reset Password
                            </Button>
                        </div>
                        
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Typography 
                                variant="body2" 
                                sx={{ color: '#0A48B3', cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => setShowMfaForm(false)}
                            >
                                Back to User ID
                            </Typography>
                        </Box>
                    </Form>
                )}
            </Formik>
        );
    };

    return (
        <Container maxWidth='xs' className={styles.mainContainer}>
            {showMfaForm ? renderMfaPasswordForm() : renderUserIdForm()}

            <Backdrop open={backdrop}>
                <CircularProgress style={{ color: 'white' }} />
            </Backdrop>
        </Container>
    );
};

export default PasswordResetForm;